import {
  Component,
  inject,
  signal,
  viewChild,
  ElementRef,
  Signal,
  WritableSignal
} from '@angular/core';

import { DateTimeFormat, DateFormatterService } from '../../../services/date/date-formatter.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { IconComponent } from '../../icon/icon.component';
import { FocusOnDisabledDirective } from '../../focus/focus-on-disabled.directive';
import { DatePickerButtonComponent } from '../date-picker-button/date-picker-button.component';
import { DatePickerInnerComponent } from '../date-picker-inner/date-picker-inner.component';

import type { DateObject } from '../date-picker-inner/date-picker-inner.domain';
import type { OnInit } from '@angular/core';

/**
 * Label for dates
 */
interface DatePickerLabel {
  abbr: string;
  full: string;
}

/**
 * The Dual Month Day Picker Component
 *
 * This is the day view of the dual month date picker component
 */
@Component({
  selector: 'td-wb-dual-month-day-picker',
  templateUrl: './dual-month-day-picker.component.html',
  styleUrls: ['./dual-month-day-picker.component.scss'],
  imports: [DatePickerButtonComponent, FocusOnDisabledDirective, IconComponent, TranslatePipe]
})
export class DualMonthDayPickerComponent implements OnInit {

  // Number of days on a six-week calendar
  private static readonly NUM_DAYS: number = 42;

  readonly yearButtonRef: Signal<ElementRef | undefined> = viewChild<ElementRef>('yearButton');

  // ── DI ──────────────────────────────────────────────────────────────────
  protected readonly datePicker = inject(DatePickerInnerComponent);
  private readonly dateFormatterService = inject(DateFormatterService);

  /**
   * Array of labels
   */
  readonly labels: WritableSignal<Array<DatePickerLabel>> = signal<Array<DatePickerLabel>>([]);

  /**
   * Array of titles of day view
   */
  readonly title: WritableSignal<Array<string>> = signal<Array<string>>([]);

  /**
   * Array of dates to display in the view
   */
  readonly rowsOfFirstMonth: WritableSignal<Array<Array<DateObject>>> = signal<Array<Array<DateObject>>>([]);
  readonly rowsOfSecondMonth: WritableSignal<Array<Array<DateObject>>> = signal<Array<Array<DateObject>>>([]);

  /**
   * Array of week numbers
   */
  readonly weekNumbers: WritableSignal<Array<number> | undefined> = signal<Array<number> | undefined>(undefined);

  /**
   * Boolean for determining whether previous button should be disabled
   */
  readonly navPrevDisabled: WritableSignal<boolean> = signal<boolean>(false);

  /**
   * Boolean for determining whether next button should be disabled
   */
  readonly navNextDisabled: WritableSignal<boolean> = signal<boolean>(false);

  /**
   * This function registers handlers and initializes step
   */
  ngOnInit(): void {
    this.datePicker.step.set('dual-month-day', { months: 1 });
    this.datePicker.setRefreshViewHandler((): void => this.refreshView(), 'dual-month-day');
    this.datePicker.setCompareHandler((date1: Date, date2: Date): number => this.compareDates(date1, date2), 'dual-month-day');
    this.datePicker.refreshView();
  }

  /**
   * This function is the refresh view handler for date picker inner component
   */
  refreshView(): void {
    const datePicker = this.datePicker;
    const year = datePicker.activeDate.getFullYear();
    const firstMonth = datePicker.activeDate.getMonth();
    const secondMonth = firstMonth + 1;
    const firstDayOfFirstMonth = new Date(year, firstMonth, 1);
    const firstDayOfSecondMonth = new Date(year, secondMonth, 1);
    const lastDayOfFirstMonth = new Date(year, firstMonth + 1, 0);
    const lastDayOfSecondMonth = new Date(year, secondMonth + 1, 0);
    const differenceOfFirstMonth = 0 - firstDayOfFirstMonth.getDay();
    const differenceOfSecondMonth = 0 - firstDayOfSecondMonth.getDay();
    const numDisplayedFromPreviousMonth = differenceOfFirstMonth > 0 ? 7 - differenceOfFirstMonth : -differenceOfFirstMonth;
    const numDisplayedFromCurrentMonth = differenceOfSecondMonth > 0 ? 7 - differenceOfSecondMonth : -differenceOfSecondMonth;
    const firstDateOfFirstMonth = new Date(firstDayOfFirstMonth.getTime());
    const firstDateOfSecondMonth = new Date(firstDayOfSecondMonth.getTime());
    const activeDate = new Date(datePicker.activeDate);

    if (numDisplayedFromPreviousMonth > 0) {
      firstDateOfFirstMonth.setDate(-numDisplayedFromPreviousMonth + 1);
    }

    if (numDisplayedFromCurrentMonth > 0) {
      firstDateOfSecondMonth.setDate(-numDisplayedFromCurrentMonth + 1);
    }

    const datesOfFirstMonth: Array<Date> = this.getDates(firstDateOfFirstMonth, DualMonthDayPickerComponent.NUM_DAYS);
    const datesOfSecondMonth: Array<Date> = this.getDates(firstDateOfSecondMonth, DualMonthDayPickerComponent.NUM_DAYS);
    const daysOfFirstMonth: Array<DateObject> = this.generateDateObjects(datesOfFirstMonth, firstMonth);
    const daysOfSecondMonth: Array<DateObject> = this.generateDateObjects(datesOfSecondMonth, secondMonth);

    if (datePicker.displayDatesOfCurrentMontyOnly()) {
      this.setDateObjectsInvisible(daysOfFirstMonth, firstDayOfFirstMonth, lastDayOfFirstMonth);
      this.setDateObjectsInvisible(daysOfSecondMonth, firstDayOfSecondMonth, lastDayOfSecondMonth);
    }

    const disabled = this.setBtnDisable(firstDayOfFirstMonth);
    this.navPrevDisabled.set(disabled.navPrevDisabled);
    this.navNextDisabled.set(disabled.navNextDisabled);
    this.labels.set(this.getLabels(daysOfFirstMonth));
    this.title.set([
      datePicker.formatDate(datePicker.activeDate, datePicker.formatDayTitle()),
      datePicker.formatDate(new Date(activeDate.setMonth(secondMonth)), datePicker.formatDayTitle())
    ]);
    const rowsOfFirstMonth = datePicker.split(daysOfFirstMonth, 7) as Array<Array<DateObject>>;
    this.rowsOfFirstMonth.set(rowsOfFirstMonth);
    this.rowsOfSecondMonth.set(datePicker.split(daysOfSecondMonth, 7) as Array<Array<DateObject>>);
    this.weekNumbers.set(datePicker.showWeeks() ? this.computeWeekNumbers(rowsOfFirstMonth) : undefined);
    datePicker.updateActiveDateId([...daysOfFirstMonth, ...daysOfSecondMonth]);
  }

  /**
   * This function is the comparator for date picker inner component
   *
   * @param date1 Date to compare
   * @param date2 Date to compare
   * @returns Returns comparison of two dates
   */
  compareDates(date1: Date, date2: Date): number {
    const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
    return d1.getTime() - d2.getTime();
  }

  getAriaLabel(date: Date): string {
    return this.dateFormatterService.formatDate(date, DateTimeFormat.LONG_DATE);
  }

  private getDates(startDate: Date, n: number): Array<Date> {
    const dates = new Array<Date>(n);
    let current = new Date(startDate.getTime());
    let i = 0;
    let date: Date;
    while (i < n) {
      date = new Date(current.getTime());
      dates[i++] = date;
      current = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
    }
    return dates;
  }

  private generateDateObjects(_days: Array<Date>, month: number): Array<DateObject> {
    const datePicker = this.datePicker;
    const days: Array<DateObject> = [];
    for (let i = 0; i < DualMonthDayPickerComponent.NUM_DAYS; i++) {
      const dateObject = datePicker.createDateObject(_days[i], datePicker.formatDay());
      dateObject.secondary = _days[i].getMonth() !== month;
      dateObject.uid = datePicker.uniqueId + '-' + i;
      dateObject.isVisible = true;
      days[i] = dateObject;
    }
    return days;
  }

  private setDateObjectsInvisible(days: Array<DateObject>, firstDayOfMonth: Date, lastDayOfMonth: Date): void {
    for (let i = 0; i < DualMonthDayPickerComponent.NUM_DAYS; i++) {
      if (days[i].date < firstDayOfMonth || days[i].date > lastDayOfMonth) {
        days[i].isVisible = false;
      }
    }
  }

  private setBtnDisable(firstDayOfMonth: Date): { navPrevDisabled: boolean; navNextDisabled: boolean } {
    const datePicker = this.datePicker;
    const calendarMinDate = new Date(firstDayOfMonth.getTime());
    calendarMinDate.setHours(0);
    const navPrevDisabled = (datePicker.minDate() !== undefined) && (calendarMinDate <= datePicker.minDate());
    const calendarMaxDate = new Date(firstDayOfMonth.getTime());
    calendarMaxDate.setHours(0);
    calendarMaxDate.setMonth(calendarMaxDate.getMonth() + 1);
    const navNextDisabled = calendarMaxDate > datePicker.maxDate();
    return { navPrevDisabled, navNextDisabled };
  }

  private getLabels(days: Array<DateObject>): Array<DatePickerLabel> {
    const datePicker = this.datePicker;
    const labels: Array<DatePickerLabel> = [];
    for (let j = 0; j < 7; j++) {
      const label: DatePickerLabel = { abbr: undefined, full: undefined };
      label.abbr = datePicker.formatDate(days[j].date, datePicker.formatDayHeader());
      label.full = datePicker.formatDate(days[j].date, DateTimeFormat.DAY_OF_WEEK_FULL);
      labels.push(label);
    }
    return labels;
  }

  private computeWeekNumbers(rowsOfFirstMonth: Array<Array<DateObject>>): Array<number> {
    const datePicker = this.datePicker;
    const weekNumbers: Array<number> = [];
    const thursdayIndex = (4 + 7 - datePicker.startingDay()) % 7;
    const numWeeks = rowsOfFirstMonth.length;
    for (let curWeek = 0; curWeek < numWeeks; curWeek++) {
      weekNumbers.push(this.getISO8601WeekNumber(rowsOfFirstMonth[curWeek][thursdayIndex].date));
    }
    return weekNumbers;
  }

  private getISO8601WeekNumber(date: Date): number {
    const checkDate = new Date(date.getTime());
    let day = checkDate.getDay();
    day = day === 0 ? 7 : day;
    checkDate.setDate(checkDate.getDate() + 4 - day);
    const time = checkDate.getTime();
    checkDate.setMonth(0);
    checkDate.setDate(1);
    return Math.floor(Math.round((time - checkDate.getTime()) / 86400000) / 7) + 1;
  }

}
